/**
 * VÉRAULT — CMSLivePage wrapper
 *
 * The bulletproof live-cutover layer for the public site. Every public
 * route is wrapped in <CMSLivePage slug="home" Fallback={HomePage} />.
 *
 * Decision tree:
 *   1. While loading the /api/site-cms/live/:slug response → render the
 *      Fallback component IMMEDIATELY (no spinner, no flicker, no blank
 *      page). The bones MUST always render.
 *   2. If the API errors out or returns {enabled: false} → keep rendering
 *      the Fallback. Live cutover is OFF.
 *   3. If the API returns {enabled: true, blocks: [...]} → render the
 *      block-based version, wrapped in a top-level ErrorBoundary. If
 *      *anything* in the block tree throws, the Fallback comes back.
 *
 * The Fallback (existing React page) is therefore the permanent guarantee
 * that nothing in the CMS can break the live site.
 */
import React from "react";
import axios from "axios";
import SiteBlocksRenderer from "../portal/components/SiteBlocksRenderer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MEDIA_BASE = `${API}/cms/media/file`;
const liveAxios = axios.create({ baseURL: API });

class FullPageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.warn("[CMSLivePage] block tree threw; falling back to React page", err, info);
  }
  render() {
    if (this.state.hasError) {
      const { Fallback, fallbackProps } = this.props;
      return <Fallback {...(fallbackProps || {})} />;
    }
    return this.props.children;
  }
}

export default function CMSLivePage({ slug, Fallback, slotOverrides, fallbackProps }) {
  const [state, setState] = React.useState({ loaded: false, enabled: false, blocks: [] });

  React.useEffect(() => {
    let alive = true;
    liveAxios
      .get(`/site-cms/live/${slug}`, { timeout: 4000 })
      .then(({ data }) => {
        if (!alive) return;
        if (data && data.enabled && Array.isArray(data.blocks) && data.blocks.length > 0) {
          setState({ loaded: true, enabled: true, blocks: data.blocks });
        } else {
          setState({ loaded: true, enabled: false, blocks: [] });
        }
      })
      .catch(() => {
        if (alive) setState({ loaded: true, enabled: false, blocks: [] });
      });
    return () => { alive = false; };
  }, [slug]);

  // While the live config is still loading we render the Fallback so the
  // bones are visible from the first frame. Same for cutover-off.
  if (!state.loaded || !state.enabled) {
    return <Fallback {...(fallbackProps || {})} />;
  }

  return (
    <FullPageErrorBoundary Fallback={Fallback} fallbackProps={fallbackProps}>
      <SiteBlocksRenderer
        blocks={state.blocks}
        api={liveAxios}
        mediaBase={MEDIA_BASE}
        slotOverrides={slotOverrides}
      />
    </FullPageErrorBoundary>
  );
}
